import React, { useEffect } from 'react';
import { MinusIcon, PlusIcon } from '@dynamika-shared/ui/icons';
import { SectionCollapsible } from '@dynamika-shared/ui/CollapsibleFormSection/SectionCollapsible';
import { InnerCollapsible } from '@dynamika-shared/ui/CollapsibleFormSection/InnerCollapsible';
import { useSectionGroups } from './useSectionGroups';
import { TimelineSectionRow } from './TimelineSectionRow';
import {
  reopenSupportSubjectTaskSuccess,
  useSupportSubjectsListSlice,
} from '@restore/SupportSubject/SupportSubject.slice';
import { useParams } from 'react-router-dom';
import { SupportUpload } from '@dynamika-shared/types/subjects';
import { buildSectionsFromVisits } from './SupportSubjectSections';
import { SupportSubjectDetailViewProps } from './common/types';
import { ValidatedActionModal } from './common/ValidatedActionModal';
import { subscribeToActions } from '@restore/helpers';

export const SupportSubjectDetailView: React.FC<
  SupportSubjectDetailViewProps
> = () => {
  const { id: clinicalTrialId, patientId } = useParams<{
    id: string;
    patientId: string;
  }>();

  const {
    uploadsByKey = {},
    uploadsLoading = false,
    uploadsError,
    visitDatesByKey = {},

    qcTasksByKey = {},
    qcTasksLoading = false,
    qcTasksError,

    readingTasksByKey = {},
    taskDefinitionsByKey = {},
    readingTasksLoading = false,
    readingTasksError,

    reopeningTaskById = {},
    reopenValidationByTaskId = {},
    reopenActionErrorByTaskId = {},
  } = useSupportSubjectsListSlice(s => s) ?? {};

  const {
    fetchSupportSubjectVisitDates,
    fetchSupportSubjectUploads,
    fetchSupportSubjectTaskDefinitions,
    fetchSupportSubjectReadingTasks,
    fetchSupportSubjectQCTasks,
    validateReopenSupportSubjectTask,
    clearReopenSupportSubjectTaskValidation,
    reopenSupportSubjectTask,
  } = useSupportSubjectsListSlice((_, actions) => actions);

  const [fetchedKey, setFetchedKey] = React.useState<string | null>(null);

  const [reopenState, setReopenState] = React.useState<{
    open: boolean;
    taskId?: string;
    visitId?: string;
  }>({ open: false });

  useEffect(() => {
    if (!clinicalTrialId || !patientId) return;

    const key = `${clinicalTrialId}:${patientId}`;
    if (fetchedKey === key) return;

    fetchSupportSubjectVisitDates({ clinicalTrialId, patientId });
    fetchSupportSubjectUploads({ clinicalTrialId, patientId });
    fetchSupportSubjectTaskDefinitions({ clinicalTrialId, patientId });
    fetchSupportSubjectReadingTasks({ clinicalTrialId, patientId });
    fetchSupportSubjectQCTasks({ clinicalTrialId, patientId });

    setFetchedKey(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, patientId, fetchedKey]);

  const key = `${clinicalTrialId ?? ''}:${patientId ?? ''}`;

  const uploads: SupportUpload[] = uploadsByKey[key] ?? [];
  const visits = visitDatesByKey[key]?.visits ?? [];

  const qcTasks = qcTasksByKey[key] ?? [];
  const readingTasks = readingTasksByKey[key] ?? [];
  const readingTaskDefinitions = taskDefinitionsByKey[key] ?? [];

  const handleReopenVisitClick = React.useCallback(
    ({ visitId, taskId }: { visitId?: string; taskId?: string }) => {
      if (!taskId) return;

      setReopenState({ open: true, visitId, taskId });
    },
    []
  );

  useEffect(() => {
    if (!reopenState.open || !clinicalTrialId || !reopenState.taskId) return;

    validateReopenSupportSubjectTask({
      clinicalTrialId,
      taskId: reopenState.taskId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clinicalTrialId,
    reopenState.open,
    reopenState.taskId,
  ]);

  const handleReopenModalCancel = React.useCallback(() => {
    if (reopenState.taskId) {
      clearReopenSupportSubjectTaskValidation({ taskId: reopenState.taskId });
    }

    setReopenState({ open: false, visitId: undefined, taskId: undefined });
  }, [reopenState.taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReopenModalConfirm = React.useCallback(
    ({ comment }: { comment: string }) => {
      if (!clinicalTrialId || !patientId || !reopenState.taskId) return;

      reopenSupportSubjectTask({
        clinicalTrialId,
        patientId,
        taskId: reopenState.taskId,
        ticket: 'support',
        comment,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clinicalTrialId, patientId, reopenState.taskId]
  );

  useEffect(() => {
    if (!reopenState.taskId) return undefined;

    const taskId = reopenState.taskId;

    return subscribeToActions(reopenSupportSubjectTaskSuccess, action => {
      if (action.payload.taskId !== taskId) return;

      clearReopenSupportSubjectTaskValidation({ taskId });
      setReopenState({ open: false, visitId: undefined, taskId: undefined });
    });
  }, [reopenState.taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reopenValidationState = reopenState.taskId
    ? reopenValidationByTaskId[reopenState.taskId]
    : undefined;

  const reopeningSubmitting =
    !!reopenState.taskId && !!reopeningTaskById[reopenState.taskId];
  const reopenSubmitError = reopenState.taskId
    ? reopenActionErrorByTaskId[reopenState.taskId]
    : undefined;

  const sections =
    clinicalTrialId && patientId
      ? buildSectionsFromVisits({
          clinicalTrialId,
          patientId,
          visits,
          uploads,
          uploadsLoading,
          uploadsError,

          qcTasks,
          qcTasksLoading,
          qcTasksError,

          readingTasks,
          readingTasksLoading,
          readingTasksError,
          readingTaskDefinitions,

          onReopenVisitClick: handleReopenVisitClick,
        })
      : [];

  const { isInnerCollapsibleOpen, toggleInner } = useSectionGroups(
    sections.map(s => ({ key: s.key, innerCollapsible: s.innerCollapsible }))
  );

  return (
    <div className='space-y-0'>
      {sections.map(section => (
        <TimelineSectionRow
          key={section.key}
          topOffsetPx={5}
          dateLabel={section.visitDateLabel}
          visitStatus={section.visitStatus}
        >
          <SectionCollapsible
            title={section.title}
            actions={section.actions}
            headerBg='bg-blueTone'
            bodyBg='bg-blueTone'
            divider={false}
            iconPosition='left'
            classNames={{
              outer: 'flex-1 min-w-0 w-full',
              container: 'mt-0',
              header: 'rounded-t-md h-auto min-h-[4.8rem] py-2 pl-7 pr-3',
              body: 'pb-2',
            }}
          >
            <div className='space-y-3'>
              {section?.content?.map(item => {
                const innerOpen = isInnerCollapsibleOpen(section.key, item.key);
                return (
                  <InnerCollapsible
                    key={`${section.key}:${item.key}`}
                    open={innerOpen}
                    onToggle={() => toggleInner(section.key, item.key)}
                    title={
                      <div className='flex w-full font-medium items-center gap-2 text-white'>
                        <span className='text-[14px]'>{item.label}</span>
                        <span className='ml-auto inline-flex items-center text-[18px]'>
                          {innerOpen ? <MinusIcon /> : <PlusIcon />}
                        </span>
                      </div>
                    }
                    indent
                    headerBg='bg-spanishGray'
                    bodyBg='bg-white'
                    iconPosition='right'
                    classNames={{
                      outer: 'rounded-md overflow-hidden',
                      header: 'h-auto min-h-7 py-1 pr-2 pl-2 gap-1 text-white',
                      body: 'p-0',
                      iconBtn: 'hidden',
                      icon: 'hidden',
                    }}
                  >
                    {item.body}
                  </InnerCollapsible>
                );
              })}
            </div>
          </SectionCollapsible>
        </TimelineSectionRow>
      ))}

      <ValidatedActionModal
        open={reopenState.open}
        onCancel={handleReopenModalCancel}
        onConfirm={handleReopenModalConfirm}
        submitting={reopeningSubmitting}
        validating={!!reopenValidationState?.loading}
        disableConfirm={
          !reopenValidationState?.validated ||
          (reopenValidationState?.errors?.length ?? 0) > 0
        }
        title='Reopen IS Task'
        errors={reopenValidationState?.errors ?? []}
        submitError={reopenSubmitError}
      />
    </div>
  );
};
